import React, { useState } from "react";
import { Badge } from "reactstrap";
import { useFundamentals, formatMarketCap } from "../hooks/useFundamentals";
import CompanyProfileModal from "./CompanyProfileModal";

interface Props {
  ticker: string;
  showMarketCap?: boolean;
  className?: string;
  /** Show an info affordance and open the full company profile modal on click. Default true. */
  clickable?: boolean;
}

/**
 * Inline sector badge + optional market-cap label.
 * Renders nothing if fundamentals are unavailable for the ticker.
 *
 * Clickable by default — opens a CompanyProfileModal with the full record
 * (industry, float shares, business description) fetched from
 * /api/fundamentals/<ticker> on demand.
 */
export default function FundamentalsChip({ ticker, showMarketCap = true, className, clickable = true }: Props) {
  const getFundamentals = useFundamentals();
  const f = getFundamentals(ticker);
  const [profileOpen, setProfileOpen] = useState(false);

  if (!f?.sector) return null;

  const content = (
    <span className={`d-inline-flex align-items-center gap-1 ${className ?? ""}`}>
      <Badge color="light" className="text-muted fw-normal" style={{ fontSize: 11 }}>
        {f.sector}
      </Badge>
      {showMarketCap && f.market_cap && (
        <span className="text-muted" style={{ fontSize: 11 }}>
          {formatMarketCap(f.market_cap)}
        </span>
      )}
      {clickable && (
        <i className="fas fa-info-circle text-muted" style={{ fontSize: 10, opacity: 0.6 }} />
      )}
    </span>
  );

  if (!clickable) return content;

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        onClick={() => setProfileOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setProfileOpen(true); }}
        style={{ cursor: "pointer" }}
        title={`View ${ticker} company profile`}
      >
        {content}
      </span>
      <CompanyProfileModal ticker={ticker} isOpen={profileOpen} toggle={() => setProfileOpen(false)} />
    </>
  );
}
